import { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { KeyRound, ShieldCheck, ShieldAlert, Copy, Check } from "lucide-react";
import {
  useCurrentUser,
  useStartMfaSetup,
  useEnableMfa,
  useDisableMfa,
} from "../lib/auth/useAuth";
import { useToast } from "../lib/toast/ToastProvider";
import { ApiError } from "../lib/api/client";
import { INPUT_CLASS } from "../lib/forms/inputClass";
import { AppShell } from "../components/AppShell";
import { PlayfulHero } from "../components/PlayfulHero";
import { SectionCard } from "../components/ui/SectionCard";
import { Button } from "../components/ui/Button";
import { PageLoader } from "../components/ui/Spinner";
import { Field } from "../components/ui/Field";
import { useChangePassword } from "../lib/api/usePassword";

/**
 * Two-factor auth enrollment.
 *
 * Enrollment is deliberately two-step (setup -> confirm a live code) so a user
 * can never lock themselves out by scanning a QR that didn't actually register
 * in their app. Backup codes are shown exactly once, on confirmation.
 */
export function SecuritySettings() {
  const { data: user, isLoading } = useCurrentUser();
  const startSetup = useStartMfaSetup();
  const enableMfa = useEnableMfa();
  const disableMfa = useDisableMfa();
  const { publish } = useToast();

  const [otpauthUri, setOtpauthUri] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState<string[] | null>(null);
  const [password, setPassword] = useState("");
  const [copied, setCopied] = useState(false);

  if (isLoading || !user) return <PageLoader />;

  const handleStart = async () => {
    try {
      const started = await startSetup.mutateAsync();
      setOtpauthUri(started.otpauthUri);
      setSecret(started.secret);
      setBackupCodes(null);
    } catch (err) {
      publish({
        variant: "error",
        title: "Could not start setup",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  const handleConfirm = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      const result = await enableMfa.mutateAsync({ code });
      setBackupCodes(result.backupCodes);
      setOtpauthUri(null);
      setSecret(null);
      setCode("");
      publish({ variant: "success", title: "Two-factor authentication enabled" });
    } catch (err) {
      publish({
        variant: "error",
        title: "That code was not accepted",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  const handleDisable = async (event: React.FormEvent) => {
    event.preventDefault();
    try {
      await disableMfa.mutateAsync({ password });
      setPassword("");
      setBackupCodes(null);
      publish({ variant: "success", title: "Two-factor authentication disabled" });
    } catch (err) {
      publish({
        variant: "error",
        title: "Could not disable two-factor",
        description: err instanceof ApiError ? err.message : undefined,
      });
    }
  };

  const copyBackupCodes = async () => {
    if (!backupCodes) return;
    try {
      await navigator.clipboard.writeText(backupCodes.join("\n"));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      publish({ variant: "error", title: "Could not copy to clipboard" });
    }
  };

  return (
    <AppShell maxWidth="3xl">
      <PlayfulHero
        decor="compact"
        art="lock"
        back={{ href: "/dashboard", label: "Back to dashboard" }}
        eyebrow="Settings"
        title="Security"
        description="Protect your account with a second factor."
      />

      <SectionCard
        className="mt-8 sm:mt-10"
        icon={user.mfaEnabled ? ShieldCheck : ShieldAlert}
        title="Two-Factor Authentication"
        accent={user.mfaEnabled ? "emerald" : "amber"}
      >
        {!user.mfaAvailable ? (
          <p className="text-sm text-neutral-600">
            Two-factor authentication isn&apos;t configured on this server yet.
            Ask an administrator to set <code>MFA_SECRET_KEY</code>.
          </p>
        ) : backupCodes ? (
          <div>
            <p className="text-sm font-medium text-neutral-900">
              Save these backup codes now.
            </p>
            <p className="mt-1 text-sm text-neutral-600">
              Each one works once, if you lose your phone. This is the only time
              they will be shown.
            </p>
            <ul className="mt-3 grid grid-cols-2 gap-2 rounded-2xl bg-white/80 p-4 font-mono text-sm ring-1 ring-white">
              {backupCodes.map((c) => (
                <li key={c}>{c}</li>
              ))}
            </ul>
            <Button variant="secondary" className="mt-3" onClick={() => void copyBackupCodes()}>
              {copied ? (
                <>
                  <Check className="h-4 w-4" aria-hidden />
                  Copied
                </>
              ) : (
                <>
                  <Copy className="h-4 w-4" aria-hidden />
                  Copy codes
                </>
              )}
            </Button>
          </div>
        ) : user.mfaEnabled ? (
          <div>
            <p className="text-sm text-neutral-700">
              Two-factor authentication is <strong>on</strong>.{" "}
              {user.mfaBackupCodesRemaining} backup{" "}
              {user.mfaBackupCodesRemaining === 1 ? "code" : "codes"} remaining.
            </p>
            <form onSubmit={handleDisable} className="mt-4 max-w-sm space-y-3">
              <label htmlFor="mfa-password" className="text-sm font-medium text-neutral-800">
                Confirm your password to turn it off
              </label>
              <input
                id="mfa-password"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={INPUT_CLASS}
              />
              <Button
                type="submit"
                variant="destructiveOutline"
                isLoading={disableMfa.isPending}
              >
                Turn off two-factor
              </Button>
            </form>
          </div>
        ) : otpauthUri ? (
          <div>
            <p className="text-sm text-neutral-700">
              Scan this with your authenticator app, then enter the 6-digit code
              it shows to finish.
            </p>
            <div className="mt-4 inline-block rounded-xl bg-white p-4 ring-1 ring-neutral-200">
              <QRCodeSVG value={otpauthUri} size={168} />
            </div>
            {secret && (
              <p className="mt-3 text-xs text-neutral-600">
                Can&apos;t scan? Enter this key manually:{" "}
                <code className="font-mono text-neutral-900">{secret}</code>
              </p>
            )}
            <form onSubmit={handleConfirm} className="mt-4 max-w-xs space-y-3">
              <label htmlFor="mfa-confirm" className="text-sm font-medium text-neutral-800">
                6-digit code
              </label>
              <input
                id="mfa-confirm"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                className={INPUT_CLASS}
                placeholder="123456"
              />
              <Button type="submit" isLoading={enableMfa.isPending}>
                <KeyRound className="h-4 w-4" aria-hidden />
                Confirm & enable
              </Button>
            </form>
          </div>
        ) : (
          <div>
            <p className="text-sm text-neutral-700">
              Add a second step to sign-in using an authenticator app. Recommended
              if your account holds children&apos;s medical information.
            </p>
            <Button className="mt-4" onClick={() => void handleStart()} isLoading={startSetup.isPending}>
              <KeyRound className="h-4 w-4" aria-hidden />
              Set up two-factor
            </Button>
          </div>
        )}
      </SectionCard>

      <ChangePasswordCard />
    </AppShell>
  );
}

/**
 * Changing a password from inside the app — the only route to a new password
 * that does not depend on email delivery, which matters while the mailer is
 * unconfigured.
 */
function ChangePasswordCard() {
  const change = useChangePassword();
  const { publish } = useToast();
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");

  const mismatch = confirm.length > 0 && next !== confirm;
  const tooShort = next.length > 0 && next.length < 10;
  const canSubmit = current.length > 0 && next.length >= 10 && next === confirm;

  return (
    <SectionCard className="mt-6 sm:mt-8" icon={KeyRound} title="Password" accent="sky">
      <form
        className="space-y-4"
        onSubmit={(e) => {
          e.preventDefault();
          if (!canSubmit) return;
          change.mutate(
            { currentPassword: current, newPassword: next },
            {
              onSuccess: () => {
                publish({ variant: "success", title: "Password changed" });
                setCurrent("");
                setNext("");
                setConfirm("");
              },
              onError: (err) =>
                publish({
                  variant: "error",
                  title:
                    err.code === "UNAUTHORIZED"
                      ? "That current password is incorrect"
                      : "Couldn't change your password",
                }),
            },
          );
        }}
      >
        <Field label="Current password" htmlFor="pw-current">
          <input
            id="pw-current"
            type="password"
            autoComplete="current-password"
            value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Field
          label="New password"
          htmlFor="pw-new"
          error={tooShort ? "At least 10 characters" : undefined}
        >
          <input
            id="pw-new"
            type="password"
            autoComplete="new-password"
            value={next}
            onChange={(e) => setNext(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Field
          label="Confirm new password"
          htmlFor="pw-confirm"
          error={mismatch ? "Passwords don't match" : undefined}
        >
          <input
            id="pw-confirm"
            type="password"
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className={INPUT_CLASS}
          />
        </Field>
        <Button type="submit" disabled={!canSubmit} isLoading={change.isPending}>
          Change password
        </Button>
      </form>
    </SectionCard>
  );
}
